import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _extends from '@babel/runtime/helpers/extends';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import EnterpriseIcon, { CancelIcon, CautionFilledIcon, ErrorIcon, ErrorFilledIcon, CheckCircleFilledIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React__default, { useState, useCallback, useContext, useRef, useEffect, createContext } from 'react';
import safeEscape from '../utils/safeEscape.js';
import Anchor from './Anchor.js';
import Button from './Button.js';
import Caption from './Caption.js';
import Card from './Card.js';
import Grid from './Grid.js';
import Heading from './Heading.js';

var _excluded = ["className", "children", "location"],
  _excluded2 = ["as", "autoHideDuration", "caption", "className", "data-testid", "type", "heading", "index", "location", "message", "links", "onRefuse", "onComplete", "pauseOnHover", "toastLength"],
  _excluded3 = ["children"],
  _excluded4 = ["onComplete"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Timer = function Timer(callback, delay) {
  var timerId = null;
  var start = 0;
  var remaining = delay;
  this.pause = function () {
    clearTimeout(timerId);
    timerId = null;
    remaining -= Date.now() - start;
  };
  this.resume = function () {
    if (timerId) {
      return;
    }
    start = Date.now();
    timerId = setTimeout(callback, remaining);
  };
  this.resume();
};
var Toaster = function Toaster(_ref) {
  var className = _ref.className,
    children = _ref.children,
    _ref$location = _ref.location,
    location = _ref$location === void 0 ? 'bottom' : _ref$location,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var finalClasses = classnames('C-Toaster', "--location-".concat(location), className);
  var getToast = function getToast(toast) {
    return /*#__PURE__*/React__default.createElement(Toast, toast.props);
  };
  var generateToast = function generateToast(input, location) {
    var reverse = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var data = input.map(function (child) {
      return {
        key: child.key,
        props: child.props
      };
    });
    var toaster = [];
    var populateProps = function populateProps(data, index) {
      var toast = {};
      toast.props = _objectSpread(_objectSpread({}, data[index].props), {}, {
        index: index,
        key: data[index].key || index,
        location: location,
        toastLength: data.length
      });
      toaster = [].concat(_toConsumableArray(toaster), [getToast(toast)]);
    };
    if (reverse) {
      for (var j = data.length - 1; j >= 0; j--) {
        populateProps(data, j);
      }
    } else {
      for (var i = 0; i < data.length; i++) {
        populateProps(data, i);
      }
    }
    return toaster;
  };
  var toasterChild = React__default.Children.toArray(children);
  var renderToast = children && generateToast(toasterChild, location, location === 'bottom');
  return /*#__PURE__*/React__default.createElement("div", _extends({
    className: finalClasses
  }, otherProps), renderToast);
};
Toaster.displayName = 'Toaster';
var Toast = function Toast(_ref2) {
  _ref2.as;
    var _ref2$autoHideDuratio = _ref2.autoHideDuration,
    autoHideDuration = _ref2$autoHideDuratio === void 0 ? 6000 : _ref2$autoHideDuratio,
    caption = _ref2.caption,
    className = _ref2.className,
    dataTestId = _ref2['data-testid'],
    _ref2$type = _ref2.type,
    type = _ref2$type === void 0 ? 'default' : _ref2$type,
    heading = _ref2.heading,
    index = _ref2.index,
    location = _ref2.location,
    _ref2$message = _ref2.message,
    message = _ref2$message === void 0 ? 'Something went wrong.' : _ref2$message,
    links = _ref2.links,
    onRefuse = _ref2.onRefuse,
    onComplete = _ref2.onComplete,
    pauseOnHover = _ref2.pauseOnHover,
    toastLength = _ref2.toastLength,
    otherProps = _objectWithoutProperties(_ref2, _excluded2);
  var accessibilityHeading = useRef( /*#__PURE__*/React__default.createRef());
  // isVisible: the toast positioned inside the viewport
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    isVisible = _useState2[0],
    setIsVisible = _useState2[1];
  var hasLinks = (links === null || links === void 0 ? void 0 : links.length) > 0;
  //if there are links accessibility requires the toast doesn't timeout
  var canPause = hasLinks ? false : pauseOnHover;
  var isTimeoutMinimumReached = autoHideDuration >= 5000;
  if (!isTimeoutMinimumReached) {
    console.error('Hey Canvas User, toast timeout minimum is 5 seconds (5000 ms autoHideDuration) for accessibility. Your timeout has been automatically updated.');
  }
  useEffect(function () {
    if ((links === null || links === void 0 ? void 0 : links.length) > 2) console.error('Toaster.Toast prop links array takes max length of 2, current length ', links === null || links === void 0 ? void 0 : links.length);
  }, [links]);
  var typeIcons = {
    alert: CautionFilledIcon,
    "default": ErrorIcon,
    error: ErrorFilledIcon,
    success: CheckCircleFilledIcon
  };
  var finalType = 'default';
  if (type === 'alert' || type === 'error' || type === 'success' || type === 'default') {
    finalType = type;
  }
  var finalClasses = classnames('C-Toaster__Toast hc-pa-normal', _defineProperty(_defineProperty(_defineProperty({}, "--type-".concat(type), finalType), "--location-".concat(location), location), "isVisible", isVisible), className);
  var hideToast = useCallback(function () {
    setIsVisible(false);
    // setTimeout is necessary or toasts may exit with no animation
    setTimeout(function () {
      onComplete === null || onComplete === void 0 ? void 0 : onComplete();
    }, 500);
  }, [onComplete]);

  // All toasts will be removed in one of two ways
  // 1. removal on refuse
  var handleRefuseToast = useCallback(function (event) {
    onRefuse === null || onRefuse === void 0 ? void 0 : onRefuse(event);
    hideToast();
  }, [onRefuse, hideToast]);

  // 2. timed auto-removal
  var timer = useRef();
  var handleToastTimer = useCallback(function () {
    //set to minimum 5 seconds per accessibility
    var timeout = isTimeoutMinimumReached ? autoHideDuration : 5000;
    // return setTimeout(() => {
    return new Timer(function () {
      hideToast();
    }, timeout);
  }, [autoHideDuration, hideToast, isTimeoutMinimumReached]);
  var handleTimerPause = function handleTimerPause() {
    if (canPause) {
      timer.current.pause();
    }
  };
  var handleTimerResume = function handleTimerResume() {
    if (canPause) {
      timer.current.resume();
    }
  };
  useEffect(function () {
    // onload check to see if it should auto-close or wait for user action
    // Disable the timer if onRefuse callback or links are present
    if (!onRefuse && !hasLinks || canPause) {
      timer.current = handleToastTimer();
      return function () {
        clearTimeout(timer.current);
      };
    }
  }, [handleToastTimer, onRefuse, hasLinks, canPause]);
  var onEscape = useCallback(function (event) {
    // safeEscape allows for the dismissal of one toast at a time, instead of all at once
    return safeEscape(handleRefuseToast, isVisible)(event);
  }, [handleRefuseToast, isVisible]);
  useEffect(function () {
    window.addEventListener('keyup', onEscape);
    return function () {
      window.removeEventListener('keyup', onEscape);
    };
  }, [onEscape]);
  var retainFocusForAccessibility = function retainFocusForAccessibility() {
    accessibilityHeading.current && accessibilityHeading.current.focus();
  };
  return /*#__PURE__*/React__default.createElement(Card, _extends({
    "data-testid": dataTestId,
    elevation: 4
  }, otherProps, {
    className: finalClasses,
    onMouseEnter: handleTimerPause,
    onMouseLeave: handleTimerResume,
    onFocus: handleTimerPause,
    onBlur: handleTimerResume,
    role: "alert"
  }), /*#__PURE__*/React__default.createElement(Grid.Container, {
    className: "hc-clr-inherit",
    spacing: "dense"
  }, /*#__PURE__*/React__default.createElement(Grid.Item, {
    className: "hc-clr-inherit"
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: typeIcons[finalType]
  })), /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: true
  }, /*#__PURE__*/React__default.createElement(Heading, {
    size: 3,
    className: "C-Toaster__Toast__Header hc-fs-section hc-lh-normal",
    ref: accessibilityHeading,
    tabIndex: "0",
    "aria-label": "".concat(heading && heading, ": Alert ").concat(index + 1, " of ").concat(toastLength)
  }, heading), caption ? /*#__PURE__*/React__default.createElement(Caption, {
    below: caption
  }, /*#__PURE__*/React__default.createElement("p", {
    className: "C-Toaster__Toast__Message"
  }, message)) : /*#__PURE__*/React__default.createElement("p", {
    className: "C-Toaster__Toast__Message"
  }, message), links === null || links === void 0 ? void 0 : links.slice(0, 2).map(function (link, index) {
    return /*#__PURE__*/React__default.createElement(Anchor, {
      key: index,
      "data-testid": dataTestId ? "".concat(dataTestId, "_link_0").concat(index) : null,
      className: "C-Toaster__Toast__Message",
      as: "button",
      type: "inline",
      onClick: link.onClick
    }, link.text);
  })), /*#__PURE__*/React__default.createElement(Grid.Item, null, /*#__PURE__*/React__default.createElement(Button, {
    iconOnly: true,
    "aria-label": "Close Toaster",
    className: "C-Toaster__Toast__Exit hc-ml-dense",
    size: "dense",
    type: "ghost",
    "data-testid": dataTestId ? "".concat(dataTestId, "_refuseButton") : null,
    onClick: handleRefuseToast
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: CancelIcon
  }))), /*#__PURE__*/React__default.createElement("span", {
    tabIndex: "0",
    onFocus: retainFocusForAccessibility
  })));
};
Toast.displayName = 'Toaster.Toast';
var ToastContext = /*#__PURE__*/createContext(function () {
  return console.error('Hey Canvas User! The useToaster function is being used outside the boundaries of ToastProvider.');
});
var ToastProvider = function ToastProvider(_ref3) {
  var children = _ref3.children,
    props = _objectWithoutProperties(_ref3, _excluded3);
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    toasts = _useState4[0],
    setToasts = _useState4[1];
  var removeToast = useCallback(function (toRemove) {
    setToasts(function (toasts) {
      return toasts.filter(function (current) {
        return toRemove !== current.key;
      });
    });
  }, [setToasts]);
  var count = 0;
  var makeToast = useCallback(function (_ref4) {
    var _onComplete = _ref4.onComplete,
      props = _objectWithoutProperties(_ref4, _excluded4);
    ++count;
    var key = props.key || "c-toaster-".concat(count);
    setToasts(function (toasts) {
      return [].concat(_toConsumableArray(toasts), [_objectSpread(_objectSpread({}, props), {}, {
        key: key,
        onComplete: function onComplete() {
          removeToast(key);
          _onComplete === null || _onComplete === void 0 ? void 0 : _onComplete();
        }
      })]);
    });
  }, [count, removeToast]);
  return /*#__PURE__*/React__default.createElement(ToastContext.Provider, {
    value: makeToast
  }, children, /*#__PURE__*/React__default.createElement(Toaster, props, toasts.map(function (props) {
    return /*#__PURE__*/React__default.createElement(Toast, props);
  })));
};
var useToaster = function useToaster() {
  return useContext(ToastContext);
};
ToastProvider.useToaster = useToaster;

export { Toast, Toaster, ToastProvider as default, useToaster };
